import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  GenericDataFormContext,
  LOADING_SAVED_QUOTATIONS_STATES,
  LoadSavedQuotations,
  QuotationInfoContext,
} from '../../../../../shared';
import { StepThreeFormContext } from './StepThreeFormContext';
import { StepTwoFormContext } from '../../../StepTwo/contexts/StepTwoForm';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm';

import {
  CarQuotationCoverage,
  DECISION_TYPE,
  GetCoverageDetail,
} from '../../../../../../../../../shared/interfaces';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../../shared/interfaces/loading-renewal-quotation-states-enum';
import { LoadQuotationToRenewResponse } from '../../../../../shared/interfaces/load-renewal-quotation-response-interface';

import ProducersService from '../../../../../../../../../shared/services/producers';

export interface CarPaymentMethodForm {
  [key: string]: string | number;
  paymentMethod: string;
  comission: number;
  extraVariable: number;
  variation: number;
}

export interface CoverageDetailByCode {
  coverageCode: string, 
  coverageDetail: GetCoverageDetail
}

export const StepThreeFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const { loadSaveQuotationState, savedQuotation, setLoadSaveQuotationState, renewalQuotationState, setRenewalQuotationState, quotationToRenewal, quotationInfo } =
    useContext(QuotationInfoContext);
  const { setCanShowSpinner } = useContext(GenericDataFormContext);
  const {
    reQuotationResponseByCar,
    quotationResponseByCar,
    stepTwoForm,
    stepThreeForm,
    coverageSelectedByVehicle,
    selectCoveragePerVehicle,
    commertialConditions,
  } = useContext(WizardDataFormContext);

  const { accessoryTable, executeCarReQuotations } =
    useContext(StepTwoFormContext);

  const [ allCoveragesDetailsByCode, setAllCoveragesDetailsByCode] = useState<CoverageDetailByCode[]>([])

  const getMultiplicativeFactorsPerCoverageByVehicle = useCallback(
    (componentId: string, coverageCode: string) => {      
      if (reQuotationResponseByCar.length) {
        return reQuotationResponseByCar
          .find((el) => String(el.componentId) === String(componentId))
          ?.reQuotationResponse.coberturas.find(
            (el) => el.codigo === coverageCode
          )?.factoresMultiplicativos;
      }

      return quotationResponseByCar
        .find((el) => String(el.componentId) === String(componentId))
        ?.quotationResponse.coberturas.find((el) => el.codigo === coverageCode)
        ?.factoresMultiplicativos;
    },
    [reQuotationResponseByCar, quotationResponseByCar]
  );

  const getInsuredSumaPerVehicle = useCallback(
    (componentId: string) => {
      const infoValueCar =
        stepTwoForm?.getFieldValue(`infoValueCar-tab-${componentId}`) ?? 0;
      const gncValueCar =
        stepTwoForm?.getFieldValue(`gncValue-tab-${componentId}`) ?? 0;

      const accessoriesValues =
        accessoryTable[componentId]?.reduce((a, b) => a + Number(b.price), 0) ??
        0;

      return Number(infoValueCar) + Number(gncValueCar) + accessoriesValues;
    },
    [stepTwoForm, accessoryTable]
  );

  const getSelectedCoverageByDefault = useCallback(
    (componentId: string) => {
      let coverages: Array<CarQuotationCoverage>;      
      if (reQuotationResponseByCar.length) {
        coverages =
          reQuotationResponseByCar.find(
            (el) => String(el.componentId) === componentId
          )?.reQuotationResponse.coberturas ?? [];
      } else {
        coverages =
          quotationResponseByCar.find(
            (el) => String(el.componentId) === componentId
          )?.quotationResponse.coberturas ?? [];
      }

      const highestPrizeCoverage = coverages?.reduce(
        (max, coverage) =>
          Number(coverage?.premio) > Number(max.premio) ? coverage : max,
        coverages[0]
      );

      return highestPrizeCoverage;
    },
    [reQuotationResponseByCar, quotationResponseByCar]
  );

  const autoSelectHighestPrizeCoverage = useCallback(
    async (componentId: string) => {
      if (
        coverageSelectedByVehicle.find(
          (el) => String(el.componentId) === componentId
        )
      )
        return;

      const highestPrizeCoverage = getSelectedCoverageByDefault(componentId);
      stepThreeForm?.setFieldValue(
        `codCoverage-${componentId}`,
        highestPrizeCoverage?.codigo
      );      
      await selectCoveragePerVehicle(highestPrizeCoverage?.codigo, componentId);
    },
    [
      coverageSelectedByVehicle,
      stepThreeForm,
      selectCoveragePerVehicle,
      getSelectedCoverageByDefault,
    ]
  );

  const fillStepThree = useCallback(() => {
    const { formaDePago } = savedQuotation as LoadSavedQuotations;

    const extraVariableField = `extraVariable`;
    const commissionField = `commission`;
    const paymentMethodField = `paymentMethod`;
    const variationField = `variation`;

    stepThreeForm.setFields([
      {
        name: extraVariableField,
        value: Number(formaDePago.porcentajeExtraPrimaVariable),
      },
      { name: commissionField, value: Number(formaDePago.porcentajeComision) },
      {
        name: paymentMethodField,
        value: `${formaDePago.codigo}-${formaDePago.codigoDePlanDePagos}`,
      },
      {
        name: variationField,
        value:
          Number(formaDePago.porcentajeExtraPrimaVariable) -
          Number(commertialConditions?.porcentajeExtraPrimaVariable),
      },
    ]);

    setLoadSaveQuotationState(
      LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLED
    );
  }, [
    savedQuotation,
    stepThreeForm,
    setLoadSaveQuotationState,
    commertialConditions,
  ]);

  const fillStepThreeRenewal = useCallback(() => {
    const { formaDePago } = quotationToRenewal as LoadQuotationToRenewResponse;
    
    const extraVariableField = `extraVariable`;
    const commissionField = `commission`;
    const paymentMethodField = `paymentMethod`;
    const variationField = `variation`;

    stepThreeForm.setFields([
      {
        name: extraVariableField,
        value: Number(formaDePago.porcentajeExtraPrimaVariable),
      },
      { name: commissionField, value: Number(formaDePago.porcentajeComision) },
      {
        name: paymentMethodField,
        value: `${formaDePago.codigo}-${formaDePago.codigoDePlanDePagos}`,
      },
      {
        name: variationField,
        value:
          Number(formaDePago.porcentajeExtraPrimaVariable) -
          Number(commertialConditions?.porcentajeExtraPrimaVariable),
      },
    ]);

    setRenewalQuotationState(
      LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLED_RENEWAL
    );
  }, [
    quotationToRenewal,
    stepThreeForm,
    setLoadSaveQuotationState,
    commertialConditions,
  ]);

  const selectCoveragesAfterLoadSavedQuotation = useCallback(async () => {
    try {
      const { bienes } = savedQuotation as LoadSavedQuotations;

      for (const { numeroComponente, coberturasSeleccionadasAutos = [] } of bienes) {
        const coverageSelected =
          coberturasSeleccionadasAutos.find(
            (el) => el.coberturaSeleccionada === DECISION_TYPE.YES
          ) ?? getSelectedCoverageByDefault(String(numeroComponente));
        stepThreeForm.setFieldValue(
          `codCoverage-${numeroComponente}`,
          coverageSelected?.codigo
        );
        await selectCoveragePerVehicle(
          coverageSelected?.codigo!,
          String(numeroComponente)
        );
      }
    } catch (error) {}
  }, [
    savedQuotation,
    selectCoveragePerVehicle,
    getSelectedCoverageByDefault,
    stepThreeForm,
  ]);

  const selectCoveragesAfterLoadRenew = useCallback(async () => {
    try {
      const { bienes } = quotationToRenewal as LoadQuotationToRenewResponse;

      for (const { numeroComponente, coberturasSeleccionadasAutos = [] } of bienes) {
        const coverageSelected =
          coberturasSeleccionadasAutos.find(
            (el) => el.coberturaSeleccionada === DECISION_TYPE.YES
          ) ?? getSelectedCoverageByDefault(String(numeroComponente));
        stepThreeForm.setFieldValue(
          `codCoverage-${numeroComponente}`,
          coverageSelected?.codigo
        );
        await selectCoveragePerVehicle(
          coverageSelected?.codigo!,
          String(numeroComponente)
        );
      }
    } catch (error) {}
  }, [
    quotationToRenewal,
    selectCoveragePerVehicle,
    getSelectedCoverageByDefault,
    stepThreeForm,
  ]);
  
  const getDetailCoverage = useCallback(
    async (coverageCode: string) => {
      if (allCoveragesDetailsByCode.find((el) => el.coverageCode === coverageCode))
        return;

      try {
        setCanShowSpinner(true);

        const response = await ProducersService.getDetailCoverage(
          quotationInfo?.branch!,
          quotationInfo?.article!,
          coverageCode
        );

        if (response.data) {
          setAllCoveragesDetailsByCode((prevValues) => [
            ...prevValues,
            { coverageCode, coverageDetail: response.data },
          ]);
        }
      } catch (error) {
      } finally {
        setCanShowSpinner(false);
      }
    },
    [
      quotationInfo,
      setCanShowSpinner,
      allCoveragesDetailsByCode,
      setAllCoveragesDetailsByCode,
    ]
  );
  
  /**
   * Cotizaciones guardadas
   */

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED
    ) {
      fillStepThree();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING
    ) {
      executeCarReQuotations();
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
        LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING &&
      reQuotationResponseByCar.length &&
      !coverageSelectedByVehicle.length
    ) {
      selectCoveragesAfterLoadSavedQuotation();
    }
  }, [
    loadSaveQuotationState,
    reQuotationResponseByCar,
    coverageSelectedByVehicle,
  ]);
  
  /**
   * Renovar cotizacion
   */
  
  useEffect(() => {
    if (
      renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL
    ) {
      fillStepThreeRenewal();
    }
  }, [renewalQuotationState]);



  useEffect(() => {
    if (
      renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.FINISH_LOADING_RENEWAL
    ) {            
      executeCarReQuotations();
    }
  }, [renewalQuotationState]);


  useEffect(() => {    
    if (
      renewalQuotationState ===
        LOADING_RENEWAL_QUOTATION_STATES.FINISH_LOADING_RENEWAL &&
      reQuotationResponseByCar.length &&
      !coverageSelectedByVehicle.length
    ) {      
      selectCoveragesAfterLoadRenew();
    }
  }, [
    renewalQuotationState,
    reQuotationResponseByCar,
    coverageSelectedByVehicle,
  ]);

  const values = useMemo(
    () => ({
      commertialConditions,
      allCoveragesDetailsByCode,
      getMultiplicativeFactorsPerCoverageByVehicle,
      getInsuredSumaPerVehicle,
      getSelectedCoverageByDefault,
      autoSelectHighestPrizeCoverage,
      getDetailCoverage
    }),
    [
      commertialConditions,
      allCoveragesDetailsByCode,
      getMultiplicativeFactorsPerCoverageByVehicle,
      getInsuredSumaPerVehicle,
      getSelectedCoverageByDefault,
      autoSelectHighestPrizeCoverage,
      getDetailCoverage
    ]
  );

  return (
    <StepThreeFormContext.Provider value={values}>
      {children}
    </StepThreeFormContext.Provider>
  );
};
